import './App.css';
import { AppContext, AppContextData } from '../contexts/AppContext';
import { ThemeContext, applyThemeCssVars } from '../contexts/ThemeContext';
import Layout from '../components/layout/Layout';
import MinimalLayout from '../components/layout/MinimalLayout';
import {
  createBrowserRouter,
  RouterProvider,
  useNavigation,
} from "react-router-dom";
import React, { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { LIGHT_THEME, DARK_THEME } from '../constants/ThemeConstants';
import { AuthProvider } from '../contexts/AuthContext';
import { PageSkeleton } from '../components/ui/Skeleton';
import lazyWithRetry from '../utils/lazyWithRetry';
import ProfileGate from '../components/auth/ProfileGate';

// Eager-loaded pages (critical path)
import HomePage from '../pages/homePage/HomePage';
import ErrorPage from '../pages/errorPage/ErrorPage';

// Lazy-loaded pages (wrapped with chunk-load retry to recover from stale Netlify deploys)
const ContactUsPage = lazyWithRetry(() => import('../pages/contactUsPage/ContactUsPage'), 'ContactUsPage');
const ProgramsPage = lazyWithRetry(() => import('../pages/programsPage/ProgramsPage'), 'ProgramsPage');
const AboutUsPage = lazyWithRetry(() => import('../pages/aboutUsPage/AboutUsPage'), 'AboutUsPage');
const GroupCalisthenicsPage = lazyWithRetry(() => import('../components/program/GroupCalisthenicsPageComponent'), 'GroupCalisthenicsPage');
const TermsAndConditionsPage = lazyWithRetry(() => import('../pages/termsAndConditionsPage/TermsAndConditionsPage'), 'TermsAndConditionsPage');
const ProgramPage = lazyWithRetry(() => import('../pages/programsPage/ProgramPage'), 'ProgramPage');
const DynamicProgramsPage = lazyWithRetry(() => import('../pages/programsPage/DynamicProgramsPage'), 'DynamicProgramsPage');
const DynamicProgramPage = lazyWithRetry(() => import('../pages/programsPage/DynamicProgramPage'), 'DynamicProgramPage');
const SignInPage = lazyWithRetry(() => import('../pages/authPage/SignInPage'), 'SignInPage');
const SignUpPage = lazyWithRetry(() => import('../pages/authPage/SignUpPage'), 'SignUpPage');
const CompleteProfilePage = lazyWithRetry(() => import('../pages/authPage/CompleteProfilePage'), 'CompleteProfilePage');
const BlogsPage = lazyWithRetry(() => import('../pages/blogsPage/BlogsPage'), 'BlogsPage');
const BlogPage = lazyWithRetry(() => import('../pages/blogsPage/BlogPage'), 'BlogPage');
const CoursesPage = lazyWithRetry(() => import('../pages/coursePage/CoursesPage'), 'CoursesPage');
const CoursePage = lazyWithRetry(() => import('../pages/coursePage/CoursePage'), 'CoursePage');
const ProfilePage = lazyWithRetry(() => import('../pages/profilePage/ProfilePage'), 'ProfilePage');
const PaymentStatusPage = lazyWithRetry(() => import('../pages/paymentPage/PaymentStatusPage'), 'PaymentStatusPage');
const DashboardPage = lazyWithRetry(() => import('../pages/dashboardPage/DashboardPage'), 'DashboardPage');

// Challenge pages chunk
const SixtyDayChallengePage = lazyWithRetry(() => import('../pages/challengePage/SixtyDayChallengePage'), 'SixtyDayChallengePage');
const SevenDayTrialChallengePage = lazyWithRetry(() => import('../pages/challengePage/SevenDayTrialChallengePage'), 'SevenDayTrialChallengePage');
const CheckoutPage = lazyWithRetry(() => import('../pages/challengePage/CheckoutPage'), 'CheckoutPage');
const TrialClassPage = lazyWithRetry(() => import('../pages/trialClassPage/TrialClassPage'), 'TrialClassPage');

// SEO Landing pages chunk
const CalisthenicsForWomenPage = lazyWithRetry(() => import('../pages/landingPages/CalisthenicsForWomenPage'), 'CalisthenicsForWomenPage');
const PrenatalPostnatalPage = lazyWithRetry(() => import('../pages/landingPages/PrenatalPostnatalPage'), 'PrenatalPostnatalPage');
const OnlineWorkoutIndiaPage = lazyWithRetry(() => import('../pages/landingPages/OnlineWorkoutIndiaPage'), 'OnlineWorkoutIndiaPage');
const CalisthenicsForBeginnersPage = lazyWithRetry(() => import('../pages/landingPages/CalisthenicsForBeginnersPage'), 'CalisthenicsForBeginnersPage');
const WorkingWomenFitnessPage = lazyWithRetry(() => import('../pages/landingPages/WorkingWomenFitnessPage'), 'WorkingWomenFitnessPage');
const CalisthenicsL1CertificationPage = lazyWithRetry(() => import('../pages/landingPages/CalisthenicsL1CertificationPage'), 'CalisthenicsL1CertificationPage');

// Admin pages chunk
const AdminLayout = lazyWithRetry(() => import('../components/admin/AdminLayout'), 'AdminLayout');
const AdminOverviewPage = lazyWithRetry(() => import('../pages/admin/AdminOverviewPage'), 'AdminOverviewPage');
const AdminUsersPage = lazyWithRetry(() => import('../pages/admin/AdminUsersPage'), 'AdminUsersPage');
const AdminProgramsManagementPage = lazyWithRetry(() => import('../pages/admin/AdminProgramsManagementPage'), 'AdminProgramsManagementPage');
const AdminUserDetailPage = lazyWithRetry(() => import('../pages/admin/AdminUserDetailPage'), 'AdminUserDetailPage');
const AdminOrdersPage = lazyWithRetry(() => import('../pages/admin/AdminOrdersPage'), 'AdminOrdersPage');
const AdminOrderDetailPage = lazyWithRetry(() => import('../pages/admin/AdminOrderDetailPage'), 'AdminOrderDetailPage');
const AdminBlogsPage = lazyWithRetry(() => import('../pages/admin/content/AdminBlogsPage'), 'AdminBlogsPage');
const AdminBlogFormPage = lazyWithRetry(() => import('../pages/admin/content/AdminBlogFormPage'), 'AdminBlogFormPage');
const AdminProgramsPage = lazyWithRetry(() => import('../pages/admin/content/AdminProgramsPage'), 'AdminProgramsPage');
const AdminProgramFormPage = lazyWithRetry(() => import('../pages/admin/content/AdminProgramFormPage'), 'AdminProgramFormPage');
const AdminCoursesPage = lazyWithRetry(() => import('../pages/admin/content/AdminCoursesPage'), 'AdminCoursesPage');
const AdminCourseFormPage = lazyWithRetry(() => import('../pages/admin/content/AdminCourseFormPage'), 'AdminCourseFormPage');
const AdminProductsPage = lazyWithRetry(() => import('../pages/admin/content/AdminProductsPage'), 'AdminProductsPage');
const AdminProductFormPage = lazyWithRetry(() => import('../pages/admin/content/AdminProductFormPage'), 'AdminProductFormPage');
const AdminSubscribersPage = lazyWithRetry(() => import('../pages/admin/AdminSubscribersPage'), 'AdminSubscribersPage');
const AdminActivityLogPage = lazyWithRetry(() => import('../pages/admin/AdminActivityLogPage'), 'AdminActivityLogPage');
const AdminScheduledMessagesPage = lazyWithRetry(() => import('../pages/admin/AdminScheduledMessagesPage'), 'AdminScheduledMessagesPage');
const AdminSiteConfigPage = lazyWithRetry(() => import('../pages/admin/content/AdminSiteConfigPage'), 'AdminSiteConfigPage');

const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<PageSkeleton />}>
    {children}
  </Suspense>
);

function App() {
  const [theme, setTheme] = React.useState(() => {
    const saved = localStorage.getItem("theme") || DARK_THEME;
    applyThemeCssVars(saved);
    return saved;
  });

  const toggleTheme = () => {
    const newTheme = theme === LIGHT_THEME ? DARK_THEME : LIGHT_THEME;
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    applyThemeCssVars(newTheme);
  };

  const LayoutWrapper = ({ children }) => {
    const navigation = useNavigation();
    return (
      <Layout toggleTheme={toggleTheme}>
        {navigation.state === "loading" && <PageSkeleton />}
        {navigation.state === "idle" && (
          <SuspenseWrapper><ProfileGate>{children}</ProfileGate></SuspenseWrapper>
        )}
      </Layout>
    );
  };

  const MinimalLayoutWrapper = ({ children }) => {
    const navigation = useNavigation();
    return (
      <MinimalLayout>
        {navigation.state === "loading" && <PageSkeleton />}
        {navigation.state === "idle" && (
          <SuspenseWrapper><ProfileGate>{children}</ProfileGate></SuspenseWrapper>
        )}
      </MinimalLayout>
    );
  };

  const router = createBrowserRouter([
    {
      // Pathless parent route — shared errorElement for ALL routes so any
      // chunk-load failure or render error falls back to ErrorPage (which
      // detects ChunkLoadError and auto-reloads once) instead of React
      // Router's default "Unexpected Application Error!" UI.
      errorElement: <LayoutWrapper><ErrorPage /></LayoutWrapper>,
      children: [
        {
          path: "/",
          element: <LayoutWrapper><HomePage /></LayoutWrapper>,
        },
        {
          path: "/contact-us",
          element: <LayoutWrapper><ContactUsPage /></LayoutWrapper>
        },
        {
          path: "/programs",
          element: <LayoutWrapper><DynamicProgramsPage /></LayoutWrapper>
        },
        {
          path: "/programs/explore",
          element: <LayoutWrapper><ProgramsPage /></LayoutWrapper>
        },
        {
          path: "/programs/explore/:programDisplayId/:variantDisplayId",
          element: <LayoutWrapper><ProgramPage /></LayoutWrapper>
        },
        {
          path: "/group-calisthenics",
          element: <LayoutWrapper><GroupCalisthenicsPage /></LayoutWrapper>
        },
        {
          path: "/programs/:programDisplayId/:variantDisplayId",
          element: <LayoutWrapper><DynamicProgramPage /></LayoutWrapper>
        },
        {
          path: "/about-us",
          element: <LayoutWrapper><AboutUsPage /></LayoutWrapper>
        },
        {
          path: "/terms-and-conditions",
          element: <LayoutWrapper><TermsAndConditionsPage /></LayoutWrapper>
        },
        {
          path: "/error",
          element: <LayoutWrapper><ErrorPage /></LayoutWrapper>
        },
        {
          path: "/sign-in",
          element: <LayoutWrapper><SignInPage /></LayoutWrapper>
        },
        {
          path: "/sign-up",
          element: <LayoutWrapper><SignUpPage /></LayoutWrapper>
        },
        {
          path: "/complete-profile",
          element: <LayoutWrapper><CompleteProfilePage /></LayoutWrapper>
        },
        {
          path: "/blogs",
          element: <LayoutWrapper><BlogsPage /></LayoutWrapper>
        },
        {
          path: "/blogs/:blogId",
          element: <LayoutWrapper><BlogPage /></LayoutWrapper>
        },
        {
          path: "/courses",
          element: <LayoutWrapper><CoursesPage /></LayoutWrapper>
        },
        {
          path: "/courses/:courseId",
          element: <LayoutWrapper><CoursePage /></LayoutWrapper>
        },
        {
          path: "/profile",
          element: <LayoutWrapper><ProfilePage /></LayoutWrapper>
        },
        {
          path: "/90-day-challenge",
          element: <LayoutWrapper><SixtyDayChallengePage /></LayoutWrapper>
        },
        {
          path: "/7-day-challenge",
          element: <MinimalLayoutWrapper><SevenDayTrialChallengePage /></MinimalLayoutWrapper>
        },
        {
          path: "/checkout-7-day-challenge",
          element: <MinimalLayoutWrapper><CheckoutPage /></MinimalLayoutWrapper>
        },
        {
          path: "/trial-class",
          element: <MinimalLayoutWrapper><TrialClassPage /></MinimalLayoutWrapper>
        },
        {
          path: "/payment/status",
          element: <LayoutWrapper><PaymentStatusPage /></LayoutWrapper>
        },
        {
          path: "/dashboard",
          element: <LayoutWrapper><DashboardPage /></LayoutWrapper>
        },
        // SEO Landing Pages
        {
          path: "/calisthenics-for-women",
          element: <LayoutWrapper><CalisthenicsForWomenPage /></LayoutWrapper>
        },
        {
          path: "/prenatal-postnatal-workout",
          element: <LayoutWrapper><PrenatalPostnatalPage /></LayoutWrapper>
        },
        {
          path: "/online-workout-classes-india",
          element: <LayoutWrapper><OnlineWorkoutIndiaPage /></LayoutWrapper>
        },
        {
          path: "/calisthenics-for-beginners",
          element: <LayoutWrapper><CalisthenicsForBeginnersPage /></LayoutWrapper>
        },
        {
          path: "/working-women-fitness",
          element: <LayoutWrapper><WorkingWomenFitnessPage /></LayoutWrapper>
        },
        {
          path: "/calisthenics-l1-certification",
          element: <LayoutWrapper><CalisthenicsL1CertificationPage /></LayoutWrapper>
        },
        {
          path: "/admin",
          element: <SuspenseWrapper><ProfileGate><AdminLayout toggleTheme={toggleTheme} /></ProfileGate></SuspenseWrapper>,
          children: [
            { index: true, element: <SuspenseWrapper><AdminOverviewPage /></SuspenseWrapper> },
            { path: "users", element: <SuspenseWrapper><AdminUsersPage /></SuspenseWrapper> },
            { path: "users/:userId", element: <SuspenseWrapper><AdminUserDetailPage /></SuspenseWrapper> },
            { path: "orders", element: <SuspenseWrapper><AdminOrdersPage /></SuspenseWrapper> },
            { path: "programs-management", element: <SuspenseWrapper><AdminProgramsManagementPage /></SuspenseWrapper> },
            { path: "orders/:orderId", element: <SuspenseWrapper><AdminOrderDetailPage /></SuspenseWrapper> },
            { path: "content/blogs", element: <SuspenseWrapper><AdminBlogsPage /></SuspenseWrapper> },
            { path: "content/blogs/new", element: <SuspenseWrapper><AdminBlogFormPage /></SuspenseWrapper> },
            { path: "content/blogs/:blogId/edit", element: <SuspenseWrapper><AdminBlogFormPage /></SuspenseWrapper> },
            { path: "content/programs", element: <SuspenseWrapper><AdminProgramsPage /></SuspenseWrapper> },
            { path: "content/programs/new", element: <SuspenseWrapper><AdminProgramFormPage /></SuspenseWrapper> },
            { path: "content/programs/:programId/edit", element: <SuspenseWrapper><AdminProgramFormPage /></SuspenseWrapper> },
            { path: "content/courses", element: <SuspenseWrapper><AdminCoursesPage /></SuspenseWrapper> },
            { path: "content/courses/new", element: <SuspenseWrapper><AdminCourseFormPage /></SuspenseWrapper> },
            { path: "content/courses/:courseId/edit", element: <SuspenseWrapper><AdminCourseFormPage /></SuspenseWrapper> },
            { path: "content/products", element: <SuspenseWrapper><AdminProductsPage /></SuspenseWrapper> },
            { path: "content/products/new", element: <SuspenseWrapper><AdminProductFormPage /></SuspenseWrapper> },
            { path: "content/products/:productId/edit", element: <SuspenseWrapper><AdminProductFormPage /></SuspenseWrapper> },
            { path: "content/site-config", element: <SuspenseWrapper><AdminSiteConfigPage /></SuspenseWrapper> },
            { path: "subscribers", element: <SuspenseWrapper><AdminSubscribersPage /></SuspenseWrapper> },
            { path: "scheduled-messages", element: <SuspenseWrapper><AdminScheduledMessagesPage /></SuspenseWrapper> },
            { path: "activity-log", element: <SuspenseWrapper><AdminActivityLogPage /></SuspenseWrapper> },
          ],
        },
        // Catch-all 404 — must be last
        {
          path: "*",
          element: <LayoutWrapper><ErrorPage /></LayoutWrapper>
        },
      ],
    },
  ], {
    future: {
      v7_startTransition: true,
    },
  });

  return (
    <React.StrictMode>
      <HelmetProvider>
        <AuthProvider>
          <AppContext.Provider value={AppContextData}>
            <ThemeContext.Provider value={theme}>
              <RouterProvider router={router} />
            </ThemeContext.Provider>
          </AppContext.Provider>
        </AuthProvider>
      </HelmetProvider>
    </React.StrictMode>
  );
}

export default App;
